import { MAX_LERE_NEARBY_OFFERS } from 'api/recommendations'
import OfferRecommendations from 'components/Common/OfferRecommendations'
import Heading from 'components/Luxkit/Typography/Heading'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useOfferList from 'hooks/Offers/useOfferList'
import { useLereNearbyOffers } from 'hooks/Recommendation/useLereNearbyOffers'
import React, { useContext, useMemo } from 'react'
import useSlimMobileSearch from 'components/SearchV2/Hooks/useSlimMobileSearch'

interface Props {
  filters: App.OfferListFilters;
  enableLere?: boolean; // whether to use lere recos when there is no nearby offers
}

function HotelSearchExpandedDestinationOfferList(props: Props) {
  const { filters, enableLere = false } = props

  const showSlimMobile = useSlimMobileSearch()
  const { searchItem, occupancies, checkinDate, checkoutDate } = useContext(GlobalSearchStateContext)
  const checkInStr: string | undefined = checkinDate?.format(ISO_DATE_FORMAT)
  const checkOutStr: string | undefined = checkoutDate?.format(ISO_DATE_FORMAT)
  const placeId = searchItem?.value ?? ''

  const expandedFilters = useMemo((): App.OfferListFilters => ({
    ...filters,
    distanceGt: 0,
    distanceEq: undefined,
  }), [filters])

  const offerList = useOfferList(filters)
  const nearbyOfferList = useOfferList(expandedFilters)

  const mainOfferCount = offerList.offerIds.length

  const totalCount = offerList.offerIds.length + nearbyOfferList.offerIds.length

  // lere zero search result recos
  const canUseLereZeroResultRecos = (
    enableLere &&
    !!placeId &&
    placeId.startsWith('le_') &&
    (!offerList.fetching && mainOfferCount === 0) && // no search result in polygon
    (!nearbyOfferList.fetching && nearbyOfferList.offerIds.length === 0) // no nearby result in larger polygon
  )

  const lereNearbyOffers = useLereNearbyOffers(canUseLereZeroResultRecos, placeId, occupancies, checkInStr, checkOutStr)
  const lereOfferList: App.OfferList | undefined = useMemo(() => {
    if (lereNearbyOffers) {
      return {
        offerIds: lereNearbyOffers.offers.map(offer => offer.offerId),
        key: 'lere-nearby-high-value-offers',
        fetching: lereNearbyOffers.state === 'loading',
      }
    }
  }, [lereNearbyOffers])

  const shouldUseLereZeroResultRecos = canUseLereZeroResultRecos && !!lereOfferList && (lereOfferList.fetching || lereOfferList.offerIds.length > 0)

  const expandedExtras = useMemo<Array<OfferListV2Item>>(() => ([{
    id: 'expanded-heading',
    position: 'start',
    element: <>
      {!shouldUseLereZeroResultRecos && <Heading variant="heading3">
        {mainOfferCount ? `More offers near ${searchItem?.format.mainText}` : `Offers near ${searchItem?.format.mainText}`}
      </Heading>}
    </>,
    show: 'hasOffers',
  }, {
    id: 'fallback-offers',
    position: 'end',
    element: <>
      <Heading variant="heading3">Our top exclusive offers</Heading>
      <OfferList
        filters={{
          offerTypes: filters.offerTypes,
        }}
        tileStyle="search"
      />
    </>,
    show: totalCount === 0 && !shouldUseLereZeroResultRecos,
  },
  ]), [searchItem?.format.mainText, filters.offerTypes, totalCount, mainOfferCount, shouldUseLereZeroResultRecos])

  return <>
    {!shouldUseLereZeroResultRecos &&
      <OfferList
        fullWidth={showSlimMobile}
        filters={expandedFilters}
        interstitials={expandedExtras}
        tileStyle="search"
      />
    }
    {shouldUseLereZeroResultRecos &&
      <OfferRecommendations
        useList
        trackingDescription="Zero Hotel Search Results - high value offers - no nearby"
        overrideOfferList={lereOfferList}
        maxOffers={MAX_LERE_NEARBY_OFFERS}
      />
    }
  </>
}

export default HotelSearchExpandedDestinationOfferList
